/**
 * 绑定学生平板（与 iOS Me/BindViews.swift 一致）：
 * 1. 扫码（getUserMedia + jsQR，识别 kidsplanner://bind?c=… 或直接的绑定码）或手动输入绑定码，也可以从照片识别；
 * 2. 确认绑定（设计稿 21）：GET binding-sessions/by-code/{code} 核对设备 → 选孩子（或新建）→ 设备名称 → 确认。
 * 网页版没有生物验证，确认前用 confirm() 再确认一次。
 */
import { Camera, Check, ChevronLeft, ImageUp, Keyboard, Lock, Tablet, TriangleAlert, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api, ApiError, confirm, refreshDevices, run, setSelectedChild, toast, useChildren, useCurrentChild, useStore } from "../../core";
import { Avatar, Button, EmptyHint, IconButton, PillSegment, Sheet, SheetBody, Spinner, TextField } from "../../ui";
import { ChildEditSheet } from "./ChildEditSheet";
import { parseBindCode } from "./util";

interface BindingInfo {
  status: string;
  deviceInfo?: { platform?: string | null; model?: string | null; name?: string | null; osVersion?: string | null; appVersion?: string | null } | null;
  expiresAt?: number | null;
}

export function BindSheet({ open, onClose, initialCode }: { open: boolean; onClose: () => void; initialCode?: string | null }) {
  return (
    <Sheet open={open} onClose={onClose} width={520} persistent label="绑定平板">
      <BindFlow onClose={onClose} initialCode={initialCode ?? null} />
    </Sheet>
  );
}

function BindFlow({ onClose, initialCode }: { onClose: () => void; initialCode: string | null }) {
  const [code, setCode] = useState<string | null>(initialCode);
  return code ? <BindConfirm code={code} onBack={() => setCode(null)} onClose={onClose} /> : <ScanStep onCode={setCode} onClose={onClose} />;
}

function BindHeader({ title, onBack, onClose }: { title: string; onBack?: () => void; onClose: () => void }) {
  return (
    <div className="bind-head">
      {onBack ? (
        <IconButton label="返回" size={36} onClick={onBack}>
          <ChevronLeft size={20} strokeWidth={2.6} />
        </IconButton>
      ) : (
        <span className="bind-head-gap" />
      )}
      <h2>{title}</h2>
      <IconButton label="关闭" size={36} onClick={onClose}>
        <X size={18} strokeWidth={2.6} />
      </IconButton>
    </div>
  );
}

// ---------- 1. 扫码 / 手动输入 ----------

/** 手机（触屏）默认打开摄像头；桌面默认手动输入 */
function defaultMode(): "camera" | "manual" {
  const hasCamera = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
  const touch = typeof matchMedia !== "undefined" && matchMedia("(pointer: coarse)").matches;
  return hasCamera && touch ? "camera" : "manual";
}

function ScanStep({ onCode, onClose }: { onCode: (code: string) => void; onClose: () => void }) {
  const [mode, setMode] = useState<"camera" | "manual">(defaultMode);
  const [manual, setManual] = useState("");
  const [busyImage, setBusyImage] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  function submitManual() {
    const c = parseBindCode(manual);
    if (c) onCode(c);
    else toast("绑定码格式不对", { error: true });
  }

  async function fromImage(file: File) {
    setBusyImage(true);
    try {
      const text = await decodeImage(file);
      const c = text ? parseBindCode(text) : null;
      if (c) onCode(c);
      else toast(text ? "这不是绑定二维码" : "没有在照片里找到二维码", { error: true });
    } catch {
      toast("读取照片失败", { error: true });
    } finally {
      setBusyImage(false);
    }
  }

  return (
    <>
      <BindHeader title="绑定平板" onClose={onClose} />
      <SheetBody gap={14}>
        <PillSegment
          stretch
          items={[
            ["camera", "扫码"],
            ["manual", "输入绑定码"],
          ]}
          value={mode}
          onChange={setMode}
        />
        {mode === "camera" ? (
          <QrScanner onCode={onCode} onManual={() => setMode("manual")} />
        ) : (
          <form
            className="bind-manual"
            onSubmit={(e) => {
              e.preventDefault();
              submitManual();
            }}
          >
            <p>输入平板「绑定家长手机」页面上显示的绑定码</p>
            <TextField label="绑定码" placeholder="绑定码或 kidsplanner://bind?c=…" value={manual} onChange={setManual} autoFocus />
            <Button type="submit" variant="dark" block height={48} disabled={!manual.trim()}>
              下一步
            </Button>
          </form>
        )}
        <div className="bind-alt">
          <button type="button" onClick={() => fileInput.current?.click()} disabled={busyImage}>
            {busyImage ? <Spinner size={14} /> : <ImageUp size={16} />}
            从照片识别二维码
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) void fromImage(f);
            }}
          />
        </div>
      </SheetBody>
    </>
  );
}

type JsQR = typeof import("jsqr").default;
let jsQRPromise: Promise<JsQR> | null = null;
/** jsQR 按需加载，不进首屏代码 */
function loadJsQR(): Promise<JsQR> {
  jsQRPromise ??= import("jsqr").then((m) => m.default);
  return jsQRPromise;
}

/** 识别图片里的二维码，返回文字（没有返回 null） */
async function decodeImage(file: File): Promise<string | null> {
  const jsQR = await loadJsQR();
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, 1280 / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * scale));
  const h = Math.max(1, Math.round(bmp.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(bmp, 0, 0, w, h);
  bmp.close();
  const img = ctx.getImageData(0, 0, w, h);
  return jsQR(img.data, w, h, { inversionAttempts: "attemptBoth" })?.data ?? null;
}

function cameraError(e: unknown): string {
  const name = (e as { name?: string } | null)?.name;
  if (name === "NotAllowedError" || name === "SecurityError") return "没有摄像头权限。请在浏览器设置里允许使用摄像头，或手动输入绑定码。";
  if (name === "NotFoundError" || name === "OverconstrainedError") return "没有找到可用的摄像头，请手动输入绑定码。";
  if (name === "NotReadableError") return "摄像头被其它应用占用了，请关闭后重试。";
  return "无法打开摄像头，请手动输入绑定码。";
}

function QrScanner({ onCode, onManual }: { onCode: (code: string) => void; onManual: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const onCodeRef = useRef(onCode);
  onCodeRef.current = onCode;
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(true);
  const [hint, setHint] = useState<string | null>(null);

  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setError(window.isSecureContext ? "这个浏览器不支持摄像头，请手动输入绑定码。" : "当前页面不是 HTTPS，浏览器不允许使用摄像头，请手动输入绑定码。");
      setStarting(false);
      return;
    }
    let stopped = false;
    let stream: MediaStream | null = null;
    let raf = 0;
    let last = 0;
    let found = false;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    void (async () => {
      let jsQR: JsQR;
      try {
        [stream, jsQR] = await Promise.all([navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false }), loadJsQR()]);
      } catch (e) {
        if (!stopped) {
          setError(cameraError(e));
          setStarting(false);
        }
        return;
      }
      if (stopped) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      const v = video.current;
      if (!v || !ctx) return;
      v.srcObject = stream;
      await v.play().catch(() => undefined);
      setStarting(false);
      const tick = (t: number) => {
        if (stopped || found) return;
        raf = requestAnimationFrame(tick);
        if (t - last < 150 || v.readyState < 2 || !v.videoWidth) return;
        last = t;
        const scale = Math.min(1, 720 / Math.max(v.videoWidth, v.videoHeight));
        const w = Math.round(v.videoWidth * scale);
        const h = Math.round(v.videoHeight * scale);
        canvas.width = w;
        canvas.height = h;
        ctx.drawImage(v, 0, 0, w, h);
        const qr = jsQR(ctx.getImageData(0, 0, w, h).data, w, h, { inversionAttempts: "dontInvert" });
        if (!qr?.data) return;
        const c = parseBindCode(qr.data);
        if (c) {
          found = true;
          onCodeRef.current(c);
        } else {
          setHint("这不是绑定二维码");
        }
      };
      raf = requestAnimationFrame(tick);
    })();

    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  useEffect(() => {
    if (!hint) return;
    const t = setTimeout(() => setHint(null), 2000);
    return () => clearTimeout(t);
  }, [hint]);

  if (error) {
    return (
      <div className="bind-camera-error">
        <TriangleAlert size={28} />
        <p>{error}</p>
        <Button variant="dark" height={44} icon={<Keyboard size={18} />} onClick={onManual}>
          手动输入绑定码
        </Button>
      </div>
    );
  }
  return (
    <div className="bind-camera">
      <video ref={video} muted playsInline aria-label="摄像头画面" />
      <div className="bind-frame" aria-hidden />
      {starting && (
        <div className="bind-camera-starting">
          <Camera size={26} />
          <span>正在打开摄像头…</span>
        </div>
      )}
      <p className="bind-camera-tip">{hint ?? "对准平板上「绑定家长手机」页面的二维码"}</p>
    </div>
  );
}

// ---------- 2. 确认绑定 ----------

function statusText(s: string): string {
  switch (s) {
    case "expired":
      return "绑定码已过期，请在平板上刷新二维码后重新扫码";
    case "confirmed":
    case "claimed":
      return "这个绑定码已经用过了";
    default:
      return `绑定码不可用（${s}）`;
  }
}

function BindConfirm({ code, onBack, onClose }: { code: string; onBack: () => void; onClose: () => void }) {
  const children = useChildren();
  const current = useCurrentChild();
  const childrenById = useStore((s) => s.children);
  const [info, setInfo] = useState<BindingInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [childId, setChildId] = useState<string | null>(current?.id ?? children[0]?.id ?? null);
  const [deviceName, setDeviceName] = useState("");
  const [nameEdited, setNameEdited] = useState(false);
  const [busy, setBusy] = useState(false);
  const [addingChild, setAddingChild] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get<BindingInfo>(`binding-sessions/by-code/${encodeURIComponent(code)}`)
      .then((r) => alive && setInfo(r))
      .catch((e: unknown) => {
        if (!alive) return;
        if (e instanceof ApiError && (e.status === 404 || e.status === 410)) setLoadError(e.status === 410 ? "绑定码已过期，请在平板上刷新二维码" : "没有找到这个绑定码");
        else setLoadError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      alive = false;
    };
  }, [code]);

  // 没改过名字时，设备名称跟着选中的孩子变："朵朵的 iPad"
  const child = childId ? childrenById[childId] : undefined;
  const model = info?.deviceInfo?.model ?? info?.deviceInfo?.name ?? "";
  useEffect(() => {
    if (nameEdited || !child) return;
    setDeviceName(`${child.name}的 ${model.toLowerCase().includes("ipad") ? "iPad" : "平板"}`);
  }, [child, model, nameEdited]);

  async function submit() {
    if (!child) return;
    const name = deviceName.trim();
    const ok = await confirm({ title: "确认绑定这台平板？", message: `绑定给${child.name}，设备名称「${name}」。`, confirmText: "确认绑定" });
    if (!ok) return;
    setBusy(true);
    const r = await run(() => api.post(`binding-sessions/by-code/${encodeURIComponent(code)}/confirm`, { childId: child.id, deviceName: name }));
    setBusy(false);
    if (r === undefined) return;
    setDone(true);
    setSelectedChild(child.id);
    void refreshDevices();
  }

  if (loadError || !info) {
    return (
      <>
        <BindHeader title="确认绑定" onBack={onBack} onClose={onClose} />
        <SheetBody>
          {loadError ? (
            <EmptyHint icon={TriangleAlert} text={loadError}>
              <Button variant="dark" height={44} onClick={onBack}>
                重新扫码
              </Button>
            </EmptyHint>
          ) : (
            <div className="bind-loading">
              <Spinner size={24} />
            </div>
          )}
        </SheetBody>
      </>
    );
  }

  const usable = info.status === "pending";
  const title = info.deviceInfo?.model ?? info.deviceInfo?.name ?? "学生设备";
  const fill = children.length <= 1;
  return (
    <>
      <BindHeader title="确认绑定" onBack={done ? undefined : onBack} onClose={onClose} />
      <SheetBody gap={14}>
        <div className="bind-device">
          <span className={`bind-device-icon ${usable || done ? "is-ok" : "is-bad"}`}>
            <Tablet size={44} strokeWidth={1.6} />
            <i>{usable || done ? <Check size={18} strokeWidth={3} /> : <X size={18} strokeWidth={3} />}</i>
          </span>
          <h3>{title}</h3>
          {info.deviceInfo?.name && info.deviceInfo.name !== title && <small>{info.deviceInfo.name}</small>}
          <p className={done ? "is-done" : ""}>{done ? "绑定成功，平板几秒内会自动进入" : usable ? "请核对：和平板屏幕上显示的设备一致" : statusText(info.status)}</p>
        </div>

        {usable && !done && (
          <>
            <div className="bind-card">
              <span className="kp-field-label">绑定给哪个孩子</span>
              <div className={`bind-children ${fill ? "is-fill" : ""}`}>
                {children.map((c) => (
                  <button key={c.id} type="button" className={`bind-child ${c.id === childId ? "is-on" : ""}`} aria-pressed={c.id === childId} onClick={() => setChildId(c.id)}>
                    <Avatar name={c.name} size={36} />
                    <span>{c.name}</span>
                  </button>
                ))}
                <button type="button" className="bind-child is-new" onClick={() => setAddingChild(true)}>
                  + 新建孩子
                </button>
              </div>
              <TextField
                label="设备名称"
                placeholder="例如：朵朵的 iPad"
                value={deviceName}
                maxLength={50}
                onChange={(v) => {
                  setDeviceName(v);
                  setNameEdited(true);
                }}
              />
            </div>
            <p className="bind-tip">
              <Lock size={13} strokeWidth={2.6} />
              <span>绑定码 5 分钟内有效，只能用一次。绑定后这台平板只能看到{child?.name ?? "这个孩子"}的数据。</span>
            </p>
          </>
        )}

        {done ? (
          <Button variant="dark" block height={56} onClick={onClose}>
            完成
          </Button>
        ) : usable ? (
          <Button variant="dark" block height={56} busy={busy} disabled={!child || !deviceName.trim()} icon={<Lock size={18} color="var(--kp-accent)" />} onClick={() => void submit()}>
            确认绑定
          </Button>
        ) : (
          <Button variant="soft" block height={48} onClick={onBack}>
            重新扫码
          </Button>
        )}
      </SheetBody>
      <ChildEditSheet open={addingChild} child={null} onClose={() => setAddingChild(false)} onSaved={(c) => setChildId(c.id)} />
    </>
  );
}
